
import React, { ReactNode } from 'react';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { Button } from "@/components/ui/button";
import { useNavigate } from 'react-router-dom';
import { Loader2 } from "lucide-react";

interface SubscriptionGuardProps {
  children: ReactNode;
  fallbackUI?: ReactNode;
  showLoadingIndicator?: boolean;
}

export const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({ 
  children, 
  fallbackUI,
  showLoadingIndicator = true
}) => {
  const { isSubscriptionActive, isLoading, redirectToPayment } = useSubscriptionStatus();
  const navigate = useNavigate();

  // Si en cours de chargement et que l'indicateur de chargement est activé, montrer un indicateur de chargement
  if (isLoading && showLoadingIndicator) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Vérification de votre abonnement...</p>
      </div>
    );
  }

  // Si en cours de chargement et que l'indicateur de chargement est désactivé, ne rien afficher
  if (isLoading && !showLoadingIndicator) {
    return null;
  }

  // Si l'abonnement n'est pas actif, afficher l'interface de secours ou l'interface par défaut
  if (!isSubscriptionActive) {
    if (fallbackUI) {
      return <>{fallbackUI}</>;
    }

    return (
      <div className="max-w-md mx-auto my-12 p-6 rounded-lg border bg-card shadow-sm text-center space-y-6">
        <div className="space-y-3">
          <h2 className="text-2xl font-bold">Abonnement requis</h2>
          <p className="text-muted-foreground">
            Cette fonctionnalité nécessite un abonnement actif. Veuillez souscrire à un plan pour continuer.
          </p>
        </div>
        
        <div className="space-y-3">
          <Button onClick={() => navigate('/dashboard/settings', { state: { activeMenu: 'subscription' } })} className="w-full">
            Voir les plans d'abonnement
          </Button>
          <Button onClick={() => navigate('/dashboard')} variant="outline" className="w-full">
            Retour au tableau de bord
          </Button>
        </div>
      </div>
    );
  }

  // Si l'abonnement est actif, montrer le contenu normal
  return <>{children}</>;
};
